'use client'

import { useEffect, useState } from 'react'
import { Activity, CheckCircle2, Loader2, RadioTower, ShieldCheck } from 'lucide-react'

type EventSummary = {
  event: string
  total: number
  uniqueVisitors: number
}

type ModelSummary = {
  model: string
  attempts: number
  uniqueVisitors: number
}

type ProgrammerStats = {
  since: string | null
  events: EventSummary[]
  models: ModelSummary[]
}

function formatDate(value: string | null) {
  if (!value) return 'No activity yet'
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function eventStats(stats: ProgrammerStats, event: string) {
  return stats.events.find((entry) => entry.event === event) || {
    event,
    total: 0,
    uniqueVisitors: 0,
  }
}

export function AdminProgrammerStats() {
  const [authorized, setAuthorized] = useState<boolean | null>(null)
  const [stats, setStats] = useState<ProgrammerStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/admin/web-programmer/stats')
      .then(async (response) => {
        if (!response.ok) {
          setAuthorized(false)
          return null
        }
        setAuthorized(true)
        return (await response.json()) as ProgrammerStats
      })
      .then((data) => {
        if (data) setStats(data)
      })
      .catch(() => setAuthorized(false))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center rounded-2xl border border-border bg-card py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    )
  }
  if (authorized === false || !stats) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        <ShieldCheck className="mb-2 h-5 w-5 text-primary" />
        Administrator access is required to view Web Programmer analytics.
      </div>
    )
  }

  const visits = eventStats(stats, 'visit')
  const attempts = eventStats(stats, 'connect_attempt')
  const reads = eventStats(stats, 'read_success')
  const writes = eventStats(stats, 'write_success')
  const cards = [
    { label: 'Unique browsers', value: visits.uniqueVisitors, icon: Activity },
    { label: 'Tried connecting', value: attempts.uniqueVisitors, icon: RadioTower },
    { label: 'Completed reads', value: reads.uniqueVisitors, icon: CheckCircle2 },
    { label: 'Verified writes', value: writes.uniqueVisitors, icon: CheckCircle2 },
  ]

  return (
    <section className="rounded-2xl border border-border bg-card p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-wide text-primary">
            Web Programmer
          </p>
          <h2 className="mt-1 text-lg font-semibold">Beta usage</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Anonymous aggregate activity. Radio data and raw IP addresses are not recorded here.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">Tracking since {formatDate(stats.since)}</p>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <div key={label} className="rounded-xl border border-border bg-background p-4">
            <Icon className="h-4 w-4 text-primary" />
            <p className="mt-3 text-2xl font-semibold tabular-nums">{value.toLocaleString()}</p>
            <p className="mt-1 text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[560px] table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-28" />
            <col className="w-28" />
          </colgroup>
          <thead className="bg-secondary text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left">Radio model</th>
              <th className="px-4 py-2.5 text-right">Browsers</th>
              <th className="px-4 py-2.5 text-right">Attempts</th>
            </tr>
          </thead>
          <tbody>
            {stats.models.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                  No radio connection attempts recorded yet.
                </td>
              </tr>
            ) : (
              stats.models.map((model) => (
                <tr key={model.model} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{model.model}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                    {model.uniqueVisitors}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                    {model.attempts}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}
